import { CustomPropertiesType, DateString, DateTimeString } from 'types';
import { PATCHSchema, POSTSchema, Lite } from 'types/api';
import { User, UserLite } from 'types/user';
import { Org } from 'types/org';
import { OrgSupplier, OrgSupplierConnection } from 'types/suppliers';
import {
  DataSource,
  DocumentTemplate,
  PackagingOptions,
  PurchaseRule,
  RelatedRecord,
  StockDocument,
  StockLocationLite,
  StockLotLite,
  StockLotQuerySchemaLite,
} from 'types/inventory';
import {
  Offer,
  OfferWithPartId,
  OrgPart,
  PartCore,
  PartOffer,
  PartQuote,
  PartSupply,
} from 'types/part';
import { OrderStatus, OrderUpdateStatus } from 'types/order';
import _ from 'lodash';
import { TeamLite } from 'types/properties';
import { Tag } from 'types/clientV2/tag';
import {
  PurchaseRequestGroupSchemaLite,
  PurchaseRequestSchemaLite,
} from 'types/clientV2/purchaseRequests';
import { ChipColor } from 'ui-component/extended/Chip';
import {
  SupplierQuoteLineSchema,
  SupplierQuoteLineWithQuoteSchema,
} from 'types/sourcing';
import {
  OrgPartSchema,
  PART_VERSION,
  V2OrgPartQueryAndClientV2Types,
  V2OrgPartTypes,
} from 'types/clientV2/parts';
import { ProductionRunLite } from 'types/production';
import {
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';

export enum PurchaseOrderStatus {
  VOIDED = 'voided',
  CLOSED = 'closed',
  FULFILLED = 'fulfilled',
  SENT = 'sent',
  APPROVED = 'approved',
  DRAFT = 'draft',
}

export const purchaseOrderStatusLabels = {
  [PurchaseOrderStatus.VOIDED]: 'Voided',
  [PurchaseOrderStatus.CLOSED]: 'Closed',
  [PurchaseOrderStatus.FULFILLED]: 'Fulfilled',
  [PurchaseOrderStatus.SENT]: 'Sent',
  [PurchaseOrderStatus.APPROVED]: 'Approved',
  [PurchaseOrderStatus.DRAFT]: 'Draft',
};

export type PurchaseOrderCore = {
  id: string;
  dropShip: boolean;
  purchaseOrderStatus: PurchaseOrderStatus;
  orderNumber: string | null;
  poNumber: string;
  externalPoNumber: string | null;
  internalPoNumber: string | null;
  internalNotes: string | null;
  externalNotes: string | null;
  cofactrCreditApprovedAt: DateTimeString | null;
  connectionType: OrgSupplierConnection;
  archivedAt: DateTimeString | null;
  lockedAt: DateTimeString | null;
  approvedAt: DateTimeString | null;
  createdAt: DateTimeString | null;
  sentAt: DateTimeString | null;
  voidedAt: DateTimeString | null;
  fulfilledAt: DateTimeString | null;
  closedAt: DateTimeString | null;
  poFooterNotes: string | null;
  migratedToProcurementApp: boolean;
  legacyPurchase: boolean;
  externalMetadata: object | null;
  internalMetadata: object | null;
  customProperties: CustomPropertiesType;
  addressLine1: string | null;
  addressLine2: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressCountry: string | null;
  addressPostalCode: string | null;
  addressCompany: string | null;
  addressName: string | null;
  addressPhone: string | null;
  destinationLocation: StockLocationLite | null;
  org: string | null;
  processingOrg: string | null;
  orgSupplier: OrgSupplier;
  createdBy: UserLite | null;
  approvedBy: UserLite | null;
  sentBy: UserLite | null;
  voidedBy: UserLite | null;
  closedBy: UserLite | null;
  fulfilledBy: UserLite | null;
  extBillingId: string | null;
  locked: boolean;
  archived: boolean;
  approved: boolean;
  purchaseOrderDocument: StockDocument | null;
  revisionNumber: number | null;
  buyMarkupPercent: string | null;
};

export type PurchaseOrder = PurchaseOrderCore & {
  subtotal: number;
  total: number;
  shippingPaid: string;
  salesTaxPaid: string;
  dutiesPaid: string;
  otherTotalPaid: string;
  cofactrChargeTotal: number;
  mpns: string[];
  supplierOrderStatus: SupplierOrderStatus;
};

export enum SupplierOrderStatus {
  PROCESSING = 'processing',
  SENT = 'sent',
  CONFIRMED = 'confirmed',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  RECEIVED = 'received',
  CANCELED = 'canceled',
  RETURNED = 'returned',
}

export type PurchaseOrderSchemaClientV2 = PurchaseOrder & {
  dataSource: DataSource | null;
  dataSourceSyncedAt: DateTimeString | null;
  deletedAt: DateTimeString | null;
  dutiesPaid: string | null;
  extDataSourceId: string;
  externallySynced: boolean;
  lastCheckedFromKb: DateTimeString | null;
  lines: PurchaseOrderLineSchema[];
  migratedToProcurementApp: boolean;
  noPartLines: NoPartPurchaseLineSchema[];
  otherTotalPaid: string | null;
  processedBy: UserLite | null;
  salesTaxPaid: string | null;
  shippingPaid: string | null;
  supplierBills: SupplierBillSchema[];
  syncedSupplier: OrgSupplier | null;
  updatedAt: DateTimeString | null;
};

export type PurchaseOrderSchemaLite = Lite<PurchaseOrderSchemaClientV2>;

export type CreateUpdatePurchaseOrder = {
  dropShip?: boolean;
  orderNumber?: string | null;
  poNumber?: string;
  externalPoNumber?: string | null;
  internalPoNumber?: string | null;
  internalNotes?: string | null;
  externalNotes?: string | null;
  cofactrCreditApprovedAt?: DateTimeString | null;
  connectionType?: OrgSupplierConnection;
  archivedAt?: DateTimeString | null;
  lockedAt?: DateTimeString | null;
  approvedAt?: DateTimeString | null;
  sentAt?: DateTimeString | null;
  voidedAt?: DateTimeString | null;
  fulfilledAt?: DateTimeString | null;
  closedAt?: DateTimeString | null;
  poFooterNotes?: string | null;
  externalMetadata?: object | null;
  internalMetadata?: object | null;
  destinationLocation?: string | null;
  orgSupplier?: OrgSupplier['id'];
  extBillingId?: string | null;
  locked?: boolean;
  archived?: boolean;
  approved?: boolean;
  sent?: boolean;
  fulfilled?: boolean;
  closed?: boolean;
  expectedTransitTime?: number;
  shippingPaid?: number | null;
  dutiesPaid?: number | null;
  otherTotalPaid?: number | null;
  externalAdjustment?: number | null;
  internalAdjustment?: number | null;
  revisionNumber?: number | null;
  org?: string | null;
  processingOrg?: string | null;
};

export enum PurchaseRequestStatus {
  ARCHIVED = 'archived',
  VOIDED = 'voided',
  APPROVED = 'approved',
  REQUESTED = 'requested',
  RECEIVED = 'received',
  DELIVERED = 'delivered',
  SHIPPED = 'shipped',
  CONFIRMED = 'confirmed',
  PURCHASED = 'purchased',
  OPEN = 'open',
  REJECTED = 'rejected',
  IN_PO = 'in_po',
}

export const purchaseRequestStatusLabels = {
  [PurchaseRequestStatus.ARCHIVED]: 'Archived',
  [PurchaseRequestStatus.VOIDED]: 'Rejected',
  [PurchaseRequestStatus.APPROVED]: 'Approved',
  [PurchaseRequestStatus.REQUESTED]: 'Requested',
  [PurchaseRequestStatus.RECEIVED]: 'Received',
  [PurchaseRequestStatus.DELIVERED]: 'Delivered',
  [PurchaseRequestStatus.SHIPPED]: 'Shipped',
  [PurchaseRequestStatus.CONFIRMED]: 'Confirmed',
  [PurchaseRequestStatus.PURCHASED]: 'In PO',
  [PurchaseRequestStatus.IN_PO]: 'In PO',
  [PurchaseRequestStatus.OPEN]: 'Open',
  [PurchaseRequestStatus.REJECTED]: 'Rejected',
};

export enum PurchaseRequestType {
  BUY = 'buy',
  SOURCE = 'source',
  BOTH = 'both',
}

export const purchaseRequestTypeLabels = {
  [PurchaseRequestType.BUY]: 'Buy',
  [PurchaseRequestType.SOURCE]: 'Source',
  [PurchaseRequestType.BOTH]: 'Both',
};

export type PurchaseRequestCore = {
  id: string;
  quant: string;
  targetLineTotal: string | null;
  needDeliveryBy: DateString | null;
  requestedAt: DateTimeString | null;
  approvedAt: DateTimeString | null;
  assignedAt: DateTimeString | null;
  archivedAt: DateTimeString | null;
  voidedAt: DateTimeString | null;
  requestedNotes: string | null;
  assignedNotes: string | null;
  approvedNotes: string | null;
  voidedNotes: string | null;
  dropShip: boolean;
  package: string | null;
  org: string | Org;
  processingOrg: string | Org;
  requestedBy: UserLite | null;
  status: PurchaseRequestStatus;
  externalMetadata: object | null;
  internalMetadata: object | null;
  customProperties: CustomPropertiesType;
  kbOffer: object | null;
  purchaseRule: string | null;
  purchaseLines: string[];
  destinationLocation: StockLocationLite['id'] | null;
  team?: TeamLite;
  purchaseRequestGroup: string | null;
  description: string | null;
  supplierQuoteLine: SupplierQuoteLineSchema['id'] | null;
  isRequestToBuy?: boolean;
  isRequestToSource?: boolean;
  duties: string | null;
  salesTax: string | null;
  shipping: string | null;
  otherTotal: string | null;
  dimensionValue: number;
  dimensionUnit: UnitOfMeasureOptions;
  dimensionClass: DimensionClassOptions;
};

export type PurchaseRequest = PurchaseRequestCore & {
  assignedBy: UserLite | null;
  archivedBy: UserLite | null;
  assignedTo: UserLite | null;
  approvedBy: UserLite | null;
  voidedBy: UserLite | null;
  productionRun: RelatedRecord | null;
  orgSupplier: OrgSupplier | null;
  destinationLocation: StockLocationLite | null;
  partQuote: PartQuote | null;
  purchaseLines: PurchaseOrderLineCore[];
  noPartPurchaseLines: NoPartPurchaseLine[];
  purchaseRule: PurchaseRule | null;
  tags: Tag[];
  purchaseRequestGroup: PurchaseRequestGroupSchemaLite | null;
  /** --------------------------------- */
  /** --- Deprecated Part V1 Fields --- */
  /** --------------------------------- */
  part: PartCore | null;
  altParts: PartCore[];
  unapprovedAltParts: PartCore[];
  /** -------------------------- */
  /** --- New Part V2 Fields --- */
  /** -------------------------- */
  v2OrgPart: OrgPartSchema['id'];
  altV2OrgParts: OrgPartSchema['id'][];
  unapprovedAltV2OrgParts: OrgPartSchema['id'][];
  partVersion: PART_VERSION;
};

export type DecoratedPurchaseRequest = PurchaseRequest & {
  calculatedStatus?: PurchaseRequestStatus;
  supply?: PartSupply | null;
  supplyLoaded?: boolean;
  altsSupply?: PartSupply[];
  expectedTotalPrice?: number | null;
  allOffers?: OfferWithPartId[];
  supplierOffers?: OfferWithPartId[];
  noOffersError?: boolean;
  moreThanBuyableQuantError?: boolean;
  buyable?: number;
  needByError?: boolean;
  supplierQuoteLineData?: SupplierQuoteLineWithQuoteSchema | null;
  offersToUse?: {
    totalPrice: number | null | undefined;
    offers: (PartOffer | Offer)[];
    actualPurchaseQuant: number | null | undefined;
    unitPrice: number | null | undefined;
    isBackordered: boolean | null | undefined;
  } | null;
};

export type CreateUpdatePurchaseRequest = {
  id?: string;
  quant?: string | number;
  targetLineTotal?: string | number | null;
  duties?: string | number | null;
  shipping?: string | number | null;
  otherTotal?: string | number | null;
  salesTax?: string | number | null;
  needDeliveryBy?: DateString | null;
  assignedTo?: User['id'] | null;
  requestedAt?: DateTimeString | null;
  approvedAt?: DateTimeString | null;
  assignedAt?: DateTimeString | null;
  archivedAt?: DateTimeString | null;
  voidedAt?: DateTimeString | null;
  requestedNotes?: string | null;
  assignedNotes?: string | null;
  approvedNotes?: string | null;
  voidedNotes?: string | null;
  dropShip?: boolean;
  package?: string | null;
  status?: PurchaseRequestStatus;
  externalMetadata?: object | null;
  internalMetadata?: object | null;
  kbOffer?: object | null;
  purchaseRule?: string | null;
  productionRun?: string | null;
  orgSupplier?: OrgSupplier['id'] | null;
  supplierObjectId?: OrgSupplier['objectId'] | null;
  destinationLocation?: string | null;
  partQuote?: PartQuote['id'] | null;
  description?: string | null;
  approved?: boolean;
  voided?: boolean;
  archived?: boolean;
  team?: TeamLite['id'];
  purchaseRequestGroup?: PurchaseRequestGroupSchemaLite['id'] | null;
  tags?: Tag['id'][];
  supplierQuoteLine?: SupplierQuoteLineSchema['id'] | null;
  isRequestToBuy?: boolean;
  isRequestToSource?: boolean;
  printedNotes?: string | null;
  /** --------------------------------- */
  /** --- Deprecated Part V1 Fields --- */
  /** --------------------------------- */
  part?: PartCore['id'];
  altParts?: PartCore['id'][];
  unapprovedAltParts?: PartCore['id'][];
  /** -------------------------- */
  /** --- New Part V2 Fields --- */
  /** -------------------------- */
  v2OrgPart?: OrgPartSchema['id'];
  altV2OrgParts?: OrgPartSchema['id'][];
  unapprovedAltV2OrgParts?: OrgPartSchema['id'][];
  dimensionValue?: string | number;
  dimensionUnit?: UnitOfMeasureOptions;
  dimensionClass?: DimensionClassOptions;
};

export type PurchaseOrderLineCore = {
  id: string;
  part: string | null;
  description: string | null;
  quant: string;
  totalNormalizedQuant: string;
  bulkQuant: string;
  dimensionValue: string;
  dimensionUnit: UnitOfMeasureOptions;
  dimensionClass: DimensionClassOptions;
  lineTotalExpected: string;
  lineTotalPaid: string;
  shippingPaid: string | null;
  dutiesPaid: string | null;
  otherTotalPaid: string | null;
  salesTaxPaid: string | null;
  externalAdjustment: string | null;
  internalAdjustment: string | null;
  link: string | null;
  needDeliveryBy: DateString | null;
  expectedShipBy: DateString | null;
  returnableUntil: DateString | null;
  releaseDeadline: DateString | null;
  releaseDate: DateString | null;
  estimatedDelivery: DateString | null;
  promisedDeliveryBy: DateString | null;
  internalNotes: string | null;
  externalNotes: string | null;
  receivedAt: DateTimeString | null;
  shippedAt: DateTimeString | null;
  confirmedAt: DateTimeString | null;
  returnedAt: DateTimeString | null;
  canceledAt: DateTimeString | null;
  deliveredAt: DateTimeString | null;
  expectedShipsIn: number | null;
  expectedTransitTime: number | null;
  package: PackagingOptions | null;
  isBackordered: boolean;
  isUncertainLead: boolean;
  extSku: string | null;
  externalMetadata: object | null;
  internalMetadata: object | null;
  customProperties: CustomPropertiesType;
  kbOffer: object | null;
  status: OrderStatus;
  purchase: string;
  purchaseOrder: PurchaseOrderCore;
  unitPrice: number;
  extSourceId: string | null;
  supplierBillLineMatches: SupplierBillLineMatch[];
  supplierQuoteLine: SupplierQuoteLineSchema['id'] | null;
  createdAt: DateTimeString;
  updatedAt: DateTimeString;
  sortOrder: number;
  productionRun: ProductionRunLite | null;
  extBillingId: string | null;
  team: TeamLite | null;
  /** -------------------------- */
  /** --- New Part V2 Fields --- */
  /** -------------------------- */
  partVersion: PART_VERSION;
  v2OrgPart: OrgPartSchema['id'] | null;
  purchaseRequest: PurchaseRequestCore | null;
};

export type PurchaseOrderLine = Omit<PurchaseOrderLineCore, 'part'> & {
  part: PartCore | null;
  stockLots: StockLotLite[];
  purchaseRequest: PurchaseRequestCore | null;
  partQuote: PartQuote | null;
  org: string;
  productionRun: RelatedRecord | null;
  splitFrom: string | null;
};

export type PurchaseOrderLineWithPurchase = PurchaseOrderLine & {
  purchaseOrder: PurchaseOrderCore;
  events: PurchaseEvent[];
  trackingNumber: string | null;
};

export type DecoratedPurchaseOrderLine = PurchaseOrderLineWithPurchase & {
  supplierQuoteLineData?: SupplierQuoteLineWithQuoteSchema | null;
  supply?: PartSupply | null;
  supplyLoaded?: boolean;
  supplierOffers?: Offer[];
  buyable?: number;
  noOffersError?: boolean;
  moreThanBuyableQuantError?: boolean;
  moqMultipleError?: boolean;
  betterPriceError?: boolean;
  priceMismatchError?: boolean;
  skuMismatchError?: boolean;
  releaseWindowError?: boolean;
  releaseMinimumError?: boolean;
  needByError?: boolean;
  expectedTotalPrice?: number | null;
  offersToUse?: {
    totalPrice: number | null | undefined;
    offers: (PartOffer | Offer)[];
    actualPurchaseQuant: number | null | undefined;
    unitPrice: number | null | undefined;
    isBackordered: boolean | null | undefined;
  } | null;
  events?: PurchaseEvent[];
  trackingNumber?: string | null;
  purchaseOrder: PurchaseOrderCore | PurchaseOrder;
};

export type PurchaseOrderLineSchema = PurchaseOrderLineCore & {
  purchase: PurchaseOrderSchemaLite;
  orgPart: OrgPart | null;
  stockLots: StockLotLite[];
  purchaseRequest: PurchaseRequestSchemaLite | null;
};

export type PurchaseOrderLineSchemaClientV2 = Omit<
  PurchaseOrderLineSchema,
  'v2OrgPart' | 'stockLots'
> & {
  v2OrgPart: V2OrgPartQueryAndClientV2Types['v2OrgPart'];
  stockLots: StockLotQuerySchemaLite[];
  trackingNumber: string | null;
};

export type PurchaseOrderLineSchemaClientV2ExpandedV2Part = Lite<
  PurchaseOrderLineSchemaClientV2,
  'v2OrgPart'
>;

export type PurchaseOrderLineSchemaClientV2Lite =
  Lite<PurchaseOrderLineSchemaClientV2>;

export type PurchaseOrderLinePATCHSchema = PATCHSchema<PurchaseOrderLineSchema>;

export type PurchaseOrderLineSchemaLite = Lite<PurchaseOrderLineSchema>;

export type CreateUpdatePurchaseOrderLine = {
  id?: string;
  part?: string;
  description?: string;
  quant?: string | number;
  bulkQuant?: string | number;
  dimensionValue?: string | number;
  dimensionUnit?: UnitOfMeasureOptions;
  dimensionClass?: DimensionClassOptions;
  lineTotalExpected?: string | number;
  lineTotalPaid?: string | number;
  shippingPaid?: string | number | null;
  dutiesPaid?: string | number | null;
  otherTotalPaid?: string | number | null;
  externalAdjustment?: string | number | null;
  internalAdjustment?: string | number | null;
  link?: string | null;
  needDeliveryBy?: DateString | null;
  expectedShipBy?: DateString | null;
  returnableUntil?: DateString | null;
  releaseDeadline?: DateString | null;
  releaseDate?: DateString | null;
  estimatedDelivery?: DateString | null;
  promisedDeliveryBy?: DateString | null;
  internalNotes?: string | null;
  externalNotes?: string | null;
  expectedShipsIn?: string | number | null;
  expectedTransitTime?: string | number | null;
  package?: string | null;
  isBackordered?: boolean;
  isUncertainLead?: boolean;
  extSku?: string | null;
  externalMetadata?: object | null;
  internalMetadata?: object | null;
  customProperties?: object;
  kbOffer?: object | null;
  purchaseRequest?: PurchaseRequestCore['id'] | null;
  partQuote?: PartQuote['id'] | null;
  productionRun?: string | null;
  supplierQuoteLine?: SupplierQuoteLineSchema['id'] | null;
  team?: TeamLite['id'];
  sortOrder?: number;
} & Pick<V2OrgPartTypes, 'v2OrgPart'>;

export type NoPartPurchaseLine = {
  id: string;
  description: string;
  quant: string;
  unitPrice: string | null;
  lineTotal: string | null;
  externalMetadata: object | null;
  internalMetadata: object | null;
  customProperties: CustomPropertiesType;
  purchaseRequest: PurchaseRequestCore | null;
  purchaseOrder: PurchaseOrderCore;
  supplierQuoteLine: SupplierQuoteLineSchema['id'] | null;
  sortOrder: number;
};

export type CreateUpdateNoPartPurchaseLine = Partial<NoPartPurchaseLine> & {
  purchaseRequest: PurchaseRequestCore['id'];
};

export type NoPartPurchaseLineSchema = NoPartPurchaseLine & {
  purchase: PurchaseOrder;
  createdAt: DateTimeString;
  updatedAt: DateTimeString;
  extDataSourceId: string;
  dataSource: string;
  dataSourceSyncedAt: Date | string;
  externallySynced: boolean;
  supplierBillLineMatches: SupplierBillLineMatch[];
  org?: string;
};

export enum CofactrPurchaseChargeType {
  TARIFF = 'tariff',
  SHIPPING = 'shipping',
  CUSTOMS_BROKERAGE = 'customs_brokerage',
  PAYMENT = 'payment',
  INSPECTION = 'inspection',
  PURCHASE_PROTECTION = 'purchase_protection',
  SOURCING = 'sourcing',
  SUPPLIER_TRANSACTION = 'supplier_transaction',
  OTHER = 'other',
}

export const cofactrPurchaseChargeTypeOptions = _.map(
  Object.values(CofactrPurchaseChargeType),
  (v) => ({
    value: v,
    label: _.startCase(v),
  })
);

export type CofactrPurchaseCharge = {
  id: string;
  type: CofactrPurchaseChargeType;
  description: string;
  quant: string;
  unitPrice: string | null;
  lineTotal: string | null;
  externalMetadata: object | null;
  internalMetadata: object | null;
  customProperties: CustomPropertiesType;
  timestamp: DateTimeString | null;
  purchaseLines: PurchaseOrderLine['id'][];
  purchase: PurchaseOrder['id'];
  extBillingId?: string | null;
};

export type CreateUpdateCofactrPurchaseCharge = Partial<CofactrPurchaseCharge>;

export type PurchaseEvent = {
  id: string;
  quant: number | null;
  notes: string | null;
  timestamp: string;
  trackingNumber: string | null;
  trackingStatus: string | null;
  lineTotalPaid: number | null;
  salesTaxPaid: number | null;
  shippingPaid: number | null;
  otherTotalPaid: number | null;
  dutiesPaid: number | null;
  estimatedDelivery: DateString | null;
  snoozedUntil: DateString | null;
  eventType: OrderUpdateStatus;
  createdBy: UserLite | null;
  purchaseLines: PurchaseOrderLine['id'][];
  invoiceNumber: string | null;
  invoiceAmount: string | null;
};

export type SendPurchaseOrder = {
  sendTo?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  body?: string;
};

export type SendPurchaseOrderResponse = {
  success: boolean;
  message: string;
};

export type GenerateDocumentPdf = {
  documentTemplate: DocumentTemplate['id'];
  generateZip?: boolean;
};

export enum SupplierBillStatus {
  DRAFT = 'draft',
  APPROVED = 'approved',
  RECEIVED = 'received',
  PAID = 'paid',
  VOIDED = 'voided',
}

export const supplierBillStatusLabels = {
  [SupplierBillStatus.DRAFT]: 'Draft',
  [SupplierBillStatus.APPROVED]: 'Approved',
  [SupplierBillStatus.RECEIVED]: 'Received',
  [SupplierBillStatus.PAID]: 'Paid',
  [SupplierBillStatus.VOIDED]: 'Rejected',
};

export const supplierBillStatusColorMapper: { [key: string]: ChipColor } = {
  Draft: ChipColor.grey,
  Approved: ChipColor.success,
  Received: ChipColor.secondary,
  Paid: ChipColor.success,
  Voided: ChipColor.error,
  Rejected: ChipColor.error,
};

export type SupplierBillSchema = {
  id: string;
  org: Org;
  processingOrg: Org;
  orgSupplier: OrgSupplier;
  billStatus: SupplierBillStatus;
  connectionType: OrgSupplierConnection;
  invoiceNumber: string | null;
  extBillingId: string | null;
  total: string;
  totalPaid: string;
  archived: boolean;
  archivedBy: UserLite;
  archivedAt: Date | string;
  draft: boolean;
  approved: boolean;
  approvedBy: UserLite;
  approvedAt: DateTimeString;
  received: boolean;
  receivedBy: UserLite;
  receivedAt: DateTimeString;
  paid: boolean;
  paidBy: UserLite;
  paidAt: DateTimeString;
  voided: boolean;
  voidedBy: UserLite;
  voidedAt: DateTimeString | null;
  purchase: PurchaseOrder;
  createdAt: DateTimeString;
  updatedAt: DateTimeString;
  paymentDueDate: Date | string;
  customProperties: CustomPropertiesType;
  extDataSourceId: string;
  dataSource: string;
  dataSourceSyncedAt: Date | string;
  externallySynced: boolean;
  salesTax: string;
  duties: string;
  shipping: string;
  otherTotal: string;
  discounts: string;
  subTotal: string;
  cofactrFees: string;
  internalNotes: string;
  externalNotes: string;
};

export type SupplierBillSubschema = Lite<
  SupplierBillSchema,
  | 'orgSupplier'
  | 'purchase'
  | 'approvedBy'
  | 'archivedBy'
  | 'voidedBy'
  | 'org'
  | 'customProperties'
>;

export type SupplierBillPOSTSchema = POSTSchema<
  SupplierBillSchema,
  'processingOrg',
  | 'orgSupplier'
  | 'billStatus'
  | 'invoiceNumber'
  | 'extBillingId'
  | 'total'
  | 'totalPaid'
  | 'purchase'
  | 'paymentDueDate'
  | 'customProperties'
  | 'salesTax'
  | 'duties'
  | 'shipping'
  | 'otherTotal'
  | 'discounts'
  | 'subTotal'
  | 'cofactrFees'
  | 'internalNotes'
  | 'externalNotes'
>;

export type SupplierBillPATCHSchema = PATCHSchema<SupplierBillPOSTSchema>;

export type SupplierBillLineSchema = {
  id: string;
  /** -------------------------- */
  /** --- New Part V2 Fields --- */
  /** -------------------------- */
  partVersion: PART_VERSION;
  v2OrgPart: OrgPartSchema;
  /** ------------------------ */
  /** --- Remaining Fields --- */
  /** ------------------------ */
  supplierBill: SupplierBillSchema;
  description: string;
  quant: string;
  unitPrice: string;
  lineTotal: string;
  notes?: string;
  extSku?: string;
  purchaseLines: PurchaseOrderLineSchemaClientV2[];
  purchaseLineMatches: SupplierBillLineMatch[];
  updatedAt: DateTimeString;
  createdAt: DateTimeString;
  dataSource: string | null;
  dataSourceSyncedAt: DateTimeString | null;
  customProperties: CustomPropertiesType;
};

export type SupplierBillLineSubschema = Omit<
  Lite<SupplierBillLineSchema, 'customProperties' | 'v2OrgPart'>,
  'purchaseLineMatches'
> & {
  purchaseLineMatches: Lite<SupplierBillLineMatch>[];
};

type V2OrgPartConfig = {
  part?: never;
  v2OrgPart: OrgPartSchema['id'];
  description?: never;
};

type DescriptionConfig = {
  part?: never;
  v2OrgPart?: never;
  description: string;
};

export type SupplierBillLinePOSTSchema = POSTSchema<
  SupplierBillLineSchema,
  'supplierBill',
  | 'description'
  | 'quant'
  | 'unitPrice'
  | 'lineTotal'
  | 'notes'
  | 'extSku'
  | 'purchaseLines'
  | 'purchaseLineMatches'
  | 'customProperties'
> &
  (V2OrgPartConfig | DescriptionConfig);

export type SupplierBillLinePATCHSchema =
  PATCHSchema<SupplierBillLinePOSTSchema>;

export type CheckIfCanAutoApproveSupplierBillResponse = {
  id: SupplierBillSchema['id'];
  canAutoApprove: boolean;
  explanation: string;
};

export type SupplierBillLineMatchCore = {
  id: string;
  quant: number;
  purchaseLine: PurchaseOrderLineSchemaClientV2ExpandedV2Part;
  // Note: Flagship still returns a field noPartPurchaseLine
  // but it has been deprecated
  noPartPurchaseLine: Array<never>;
};

export type SupplierBillLineMatch = SupplierBillLineMatchCore & {
  supplierBillLine: SupplierBillLineSchema['id'];
  createdAt: DateTimeString;
  updatedAt: DateTimeString;
  notes?: string;
};

export type SupplierBillSchemaLite = Lite<SupplierBillSchema>;

export type PurchaseOrderLineServerSideSubSchema = Lite<
  PurchaseOrderLineSchemaClientV2,
  'purchase'
>;

export type DecoratedSupplierBillLineMatch = Omit<
  Lite<SupplierBillLineMatch>,
  'purchaseLine'
> & {
  purchaseLine: PurchaseOrderLineSchemaClientV2ExpandedV2Part | null;
  purchaseOrderStatus?: PurchaseOrderStatus;
};
